import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Stack } from '@mui/material';
import * as Yup from 'yup';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';

import { FormErrorHelperText } from '../FormErrorHelperText';
import { InviteClientPopup } from 'pages/LinkPage/components';
import { ConfirmResultEnum, IDialogContext } from 'context';
import { useDialog } from 'hooks';
import { InviteIcon } from 'assets';
import { SuccessInfoDialog } from 'components';

import styles from './MyRoomLink.module.scss';

type MyRoomLinkProps = {
  type: 'copy' | 'edit';
  value?: string;
  placeholder?: string;
  className?: string;
  onClick?: (value?: string) => void;
  showButton?: boolean;
  copyButtonRef?: React.Ref<HTMLButtonElement>;
};

export const MyRoomLink = forwardRef<unknown, MyRoomLinkProps>(
  ({ value, placeholder, className, onClick, type, copyButtonRef, ...rest }: MyRoomLinkProps, ref) => {
    const { t } = useTranslation('providerRoom');
    const { confirm, showDialog }: IDialogContext = useDialog();

    const [link, setLink] = useState(value);
    const [error, setError] = useState('');
    const inputRef: React.RefObject<HTMLDivElement> = React.useRef(null);

    useEffect(() => {
      setLink(value);
    }, [value]);

    const btnText: string = useMemo(() => {
      if (type === 'copy') {
        return t('copyLink');
      }

      return value === link && value ? t('edit') : t('save');
    }, [link, t, type, value]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      validate(value);
      setLink(value);
    };

    const handleClick = useCallback(() => {
      if (type === 'copy') {
        onClick && onClick(link);
        return;
      }

      if (value !== link && validate(link)) {
        onClick && onClick(link);
        return;
      }

      if (value === link) {
        inputRef.current!.focus();
      }
    }, [value, link]);

    const validate = (value?: string): boolean => {
      const schema = Yup.string()
        .required(t('validationRoomUrlRequired'))
        .matches(/^(([a-z]|\d|_)+([a-z]|\d|-|_)*)$/i, t('validationRoomUrlInvalidChars'))
        .max(128, t('validationMaxLength128'));

      let isValid = false;
      try {
        schema.validateSync(value);
        setError('');
        isValid = true;
      } catch (e: any) {
        setError(e.message);
      }
      return isValid;
    };

    const handleInviteClick = async (): Promise<void> => {
      const confirmationResult = await confirm({ content: InviteClientPopup });
      if (confirmationResult === ConfirmResultEnum.Confirm) {
        const modal = showDialog(SuccessInfoDialog, {
          buttonText: t('done'),
          title: t('successTitle'),
          text: t('invitationSent'),
          onConfirm: () => {
            modal.destroy();
          },
        });
      }
    };

    return (
      <Box
        ref={ref}
        {...rest}
        className={classnames(className, styles.link, {
          [styles.withInvite]: type === 'copy',
        })}
      >
        {/* Link Field - Uses SCSS for width and spacing */}
        <TextField
          placeholder={placeholder}
          inputRef={inputRef}
          disabled={type === 'copy'}
          variant='outlined'
          value={link}
          onChange={handleChange}
          className={styles.textField}
          inputProps={{
            dir: 'ltr',
          }}
          InputProps={{
            startAdornment: <InputAdornment position='start'>{window.location.origin}/</InputAdornment>,
            sx: {
              direction: 'ltr',
              unicodeBidi: 'isolate',
              '& .MuiInputBase-input': {
                direction: 'ltr',
                textAlign: 'left',
                unicodeBidi: 'plaintext',
              },
              '& .MuiInputAdornment-root': {
                direction: 'ltr',
                unicodeBidi: 'isolate',
              },
            },
          }}
        />

        {/* Button Group - Uses SCSS for layout */}
        <Stack direction='row' className={styles.buttonGroup}>
          <Button
            variant='contained'
            onClick={handleClick}
            disabled={type === 'edit' && error !== ''}
            ref={type === 'copy' ? copyButtonRef : undefined}
          >
            {btnText}
          </Button>

          {type === 'copy' && (
            <Button
              variant='outlined'
              onClick={handleInviteClick}
              startIcon={<InviteIcon />}
              sx={{
                '& .MuiButton-startIcon': {
                  marginInlineStart: 0,
                  marginInlineEnd: '0.6rem',
                },
              }}
            >
              {t('invite')}
            </Button>
          )}
        </Stack>

        <FormErrorHelperText className={styles.errorHelper}>{error}</FormErrorHelperText>
      </Box>
    );
  },
);
